import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { recordWaterUsage } from '../../../api/services/billingService';
import { getPropertyTenancyHistory } from '../../../api/services/propertyService';
import './WaterUsageView.css';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const money = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `KES ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const unitLabel = (unit) => {
  return [unit?.unitNumber, unit?.name].filter(Boolean).join(' · ') || unit?.id || 'Unit';
};

const NO_ASSIGNMENT_SENTINEL = '__NO_ASSIGNMENT__';

const assignmentIdFromTenancyRow = (row) => {
  return (
    row?.assignmentId ||
    row?.rentalAssignmentId ||
    row?.tenancyAssignmentId ||
    row?.assignment?.id ||
    row?.rentalAssignment?.id ||
    row?.id ||
    ''
  );
};

const WaterUsagePanel = ({ propertyId, units = [] }) => {
  const now = useMemo(() => new Date(), []);
  const [form, setForm] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    unitsUsed: '',
  });
  const [selectedTenantAssignmentId, setSelectedTenantAssignmentId] = useState('');
  const [activeUnitId, setActiveUnitId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [tenancyRows, setTenancyRows] = useState([]);
  const [tenancyLoading, setTenancyLoading] = useState(false);
  const [tenancyError, setTenancyError] = useState('');

  useEffect(() => {
    if (!propertyId) {
      setTenancyRows([]);
      return;
    }
    let cancelled = false;
    const loadTenants = async () => {
      setTenancyLoading(true);
      setTenancyError('');
      try {
        const res = await getPropertyTenancyHistory(propertyId);
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setTenancyRows(list);
      } catch (e) {
        if (!cancelled) {
          setTenancyRows([]);
          setTenancyError(e.message || 'Failed to fetch tenants for this property.');
        }
      } finally {
        if (!cancelled) setTenancyLoading(false);
      }
    };
    loadTenants();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const unitOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    tenancyRows.forEach((row) => {
      const id = row?.unitId || row?.unit?.id;
      if (!id) return;
      if (seen.has(id)) return;
      seen.add(id);
      const label =
        [row?.unitNumber, row?.unitName].filter(Boolean).join(' · ') ||
        [row?.unit?.unitNumber, row?.unit?.name].filter(Boolean).join(' · ') ||
        `Unit ${String(id).slice(0, 8)}`;
      options.push({
        unitId: String(id),
        label,
      });
    });

    // Fallback to units list only if tenancy history has no unit references.
    if (options.length === 0) {
      units.forEach((unit) => {
        const id = unit?.id || unit?.unitId;
        if (!id || seen.has(id)) return;
        seen.add(id);
        options.push({ unitId: String(id), label: unitLabel(unit) });
      });
    }

    return options;
  }, [tenancyRows, units]);

  const activeUnit = useMemo(() => {
    if (!activeUnitId) return null;
    return (
      unitOptions.find((u) => String(u.unitId) === String(activeUnitId)) ||
      units.find((u) => String(u?.id || u?.unitId) === String(activeUnitId)) ||
      null
    );
  }, [unitOptions, units, activeUnitId]);

  const tenantOptions = useMemo(() => {
    if (!activeUnitId) return [];

    const rowsForUnitAll = tenancyRows.filter(
      (row) => String(row?.unitId || row?.unit?.id || '') === String(activeUnitId)
    );
    const activeRows = rowsForUnitAll.filter((row) => String(row?.status || '').toUpperCase() === 'ACTIVE');
    const rowsForUnit = activeRows.length > 0 ? activeRows : rowsForUnitAll;

    const opts = rowsForUnit
      .map((row) => {
        const assignmentId = assignmentIdFromTenancyRow(row);
        const fullName = [row?.userFirstName, row?.userLastName].filter(Boolean).join(' ').trim();
        const tenantName =
          fullName ||
          row?.tenantName ||
          row?.userName ||
          row?.tenant?.fullName ||
          row?.tenant?.name ||
          row?.user?.fullName ||
          row?.user?.name ||
          row?.userEmail ||
          row?.tenantEmail ||
          'Tenant';
        const status = String(row?.status || '').toUpperCase();
        return {
          assignmentId: assignmentId ? String(assignmentId) : NO_ASSIGNMENT_SENTINEL,
          label: tenantName,
          status,
          sortRank: status === 'ACTIVE' ? 0 : 1,
        };
      })
      .sort((a, b) => a.sortRank - b.sortRank || a.label.localeCompare(b.label));

    return opts;
  }, [activeUnitId, tenancyRows]);

  useEffect(() => {
    setSelectedTenantAssignmentId('');
  }, [activeUnitId]);

  useEffect(() => {
    if (tenantOptions.length === 1) {
      setSelectedTenantAssignmentId(tenantOptions[0].assignmentId);
      return;
    }
    const active = tenantOptions.find((t) => t.status === 'ACTIVE');
    if (active) setSelectedTenantAssignmentId(active.assignmentId);
  }, [tenantOptions]);

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    return [current - 1, current, current + 1];
  }, [now]);

  const openUnitModal = (unitId) => {
    setActiveUnitId(String(unitId));
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setError('');
    setForm((prev) => ({ ...prev, unitsUsed: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const rawAssignmentId = String(selectedTenantAssignmentId || '').trim();
    const assignmentId = rawAssignmentId === NO_ASSIGNMENT_SENTINEL ? '' : rawAssignmentId;
    const unitsUsed = Number(form.unitsUsed);
    const unitsUsedInt = Math.trunc(unitsUsed);
    if (!activeUnitId) {
      setError('Please select a unit.');
      return;
    }
    if (rawAssignmentId === NO_ASSIGNMENT_SENTINEL) {
      setError('Tenant assignment is missing for this unit (cannot record water usage).');
      return;
    }
    if (!assignmentId) {
      setError('Please select a tenant for that unit.');
      return;
    }
    if (!Number.isFinite(unitsUsed) || unitsUsed < 0) {
      setError('Units used must be 0 or greater.');
      return;
    }
    if (!Number.isInteger(unitsUsed)) {
      setError('Units used must be a whole number (e.g. 3).');
      return;
    }

    setSubmitting(true);
    try {
      // Debug: compare this payload/assignment with the one that works in Postman.
      console.log('recordWaterUsage request', {
        propertyId,
        activeUnitId,
        assignmentId,
        payload: {
          year: Number(form.year),
          month: Number(form.month),
          unitsUsed: unitsUsedInt,
        },
      });
      const response = await recordWaterUsage(assignmentId, {
        year: Number(form.year),
        month: Number(form.month),
        unitsUsed: unitsUsedInt,
      });
      setResult(response?.data || null);
      setModalOpen(false);
      setForm((prev) => ({ ...prev, unitsUsed: '' }));
    } catch (err) {
      setError(err.message || 'Failed to record water usage.');
    } finally {
      setSubmitting(false);
    }
  };

  const modal = modalOpen
    ? createPortal(
        <div className="water-usage-modal-overlay" onClick={closeModal} role="presentation">
          <div className="water-usage-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="water-usage-modal__header">
              <h3>Record water usage</h3>
              <button type="button" className="water-usage-modal__close" onClick={closeModal} disabled={submitting}>
                ×
              </button>
            </div>
            <p className="water-usage-modal__unit">{activeUnit?.label || unitLabel(activeUnit) || 'Selected unit'}</p>

            <form className="water-usage-form" onSubmit={handleSubmit}>
              <div className="water-usage-field">
                <label htmlFor="water-usage-tenant-pick">Tenant</label>
                <select
                  id="water-usage-tenant-pick"
                  value={selectedTenantAssignmentId}
                  onChange={(e) => setSelectedTenantAssignmentId(e.target.value)}
                  disabled={submitting || tenantOptions.length === 0}
                  required
                >
                  <option value="">
                    {tenantOptions.length === 0 ? 'No tenants found for unit' : 'Choose tenant'}
                  </option>
                  {tenantOptions.map((opt) => (
                    <option key={opt.assignmentId} value={opt.assignmentId}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="water-usage-row">
                <div className="water-usage-field">
                  <label htmlFor="water-usage-year">Year</label>
                  <select
                    id="water-usage-year"
                    value={form.year}
                    onChange={(e) => setForm((prev) => ({ ...prev, year: Number(e.target.value) }))}
                    disabled={submitting}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="water-usage-field">
                  <label htmlFor="water-usage-month">Month</label>
                  <select
                    id="water-usage-month"
                    value={form.month}
                    onChange={(e) => setForm((prev) => ({ ...prev, month: Number(e.target.value) }))}
                    disabled={submitting}
                  >
                    {MONTHS.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="water-usage-field">
                  <label htmlFor="water-usage-units">Units used</label>
                  <input
                    id="water-usage-units"
                    type="number"
                    min="0"
                    step="any"
                    value={form.unitsUsed}
                    onChange={(e) => setForm((prev) => ({ ...prev, unitsUsed: e.target.value }))}
                    placeholder="e.g. 3"
                    disabled={submitting}
                    required
                  />
                </div>
              </div>

              {error ? <div className="water-usage-alert water-usage-alert--error">{error}</div> : null}

              <div className="water-usage-actions">
                <button type="button" className="water-usage-submit water-usage-submit--ghost" onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="water-usage-submit" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save water usage'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="water-usage-panel">
      <h2 className="water-usage-panel__title">Record water usage</h2>
      <p className="water-usage-panel__subtitle">
        Select a unit, then record year, month, and units used in the popup.
      </p>

      {tenancyError ? <div className="water-usage-alert water-usage-alert--error">{tenancyError}</div> : null}
      {tenancyLoading ? <div className="water-usage-alert">Loading units...</div> : null}

      {!tenancyLoading && unitOptions.length === 0 ? (
        <div className="water-usage-alert water-usage-alert--error">
          No units with tenancy history found for this property.
        </div>
      ) : null}

      {unitOptions.length > 0 ? (
        <div className="water-usage-units-grid">
          {unitOptions.map((unit) => (
            <button
              key={unit.unitId}
              type="button"
              className="water-usage-unit-card"
              onClick={() => openUnitModal(unit.unitId)}
            >
              <span className="water-usage-unit-card__title">{unit.label}</span>
              <span className="water-usage-unit-card__cta">Record monthly usage</span>
            </button>
          ))}
        </div>
      ) : null}

      {result ? (
        <section className="water-usage-result">
          <h3>Updated bill preview</h3>
          <div className="water-usage-result__grid">
            <div><span>Selected unit</span><strong>{activeUnit?.label || unitLabel(activeUnit) || '—'}</strong></div>
            <div><span>Tenant</span><strong>{tenantOptions.find((t) => t.assignmentId === selectedTenantAssignmentId)?.label || '—'}</strong></div>
            <div><span>Period</span><strong>{result.periodMonth || form.month}/{result.periodYear || form.year}</strong></div>
            <div><span>Water units</span><strong>{result.waterUnits ?? '—'}</strong></div>
            <div><span>Water rate</span><strong>{money(result.waterRatePerUnit)}</strong></div>
            <div><span>Water amount</span><strong>{money(result.waterAmount)}</strong></div>
            <div><span>Total due</span><strong>{money(result.totalDue)}</strong></div>
            <div><span>Total paid</span><strong>{money(result.totalPaid)}</strong></div>
            <div><span>Remaining due</span><strong>{money(result.remainingDue)}</strong></div>
            <div><span>Status</span><strong>{result.status || '—'}</strong></div>
          </div>
        </section>
      ) : null}
      {modal}
    </div>
  );
};

export default WaterUsagePanel;
